/**
 * One of the five checklist sections of a monthly (PLAN §6 step 6), with its one "All OK".
 *
 * "All OK" answers every unanswered line here OK and leaves alone the lines already answered and
 * the open items pinned above, which keep their own re-grade. A pinned line shows here as a link
 * up to it, so the section still reads complete.
 */

import { Check, CornerLeftUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { GRADE_LABEL, type ChecklistLine, type ChecklistSection as Section } from '@/lib/inspections';
import { effectiveGrade, type InspectionDraft } from '@/lib/inspectionDraft';
import { scrollToLine } from './scrollToLine';

interface Props {
    section: Section;
    lines: ChecklistLine[];
    pinned: ReadonlySet<string>;
    draft: InspectionDraft;
    onAllOk: () => void;
    renderRow: (line: ChecklistLine) => React.ReactNode;
}

export default function ChecklistSection({ section, lines, pinned, draft, onAllOk, renderRow }: Props) {
    if (lines.length === 0) return null;
    const answered = lines.filter((l) => !!draft.lines[l.key]?.grade).length;
    const canAllOk = lines.some((l) => !pinned.has(l.key) && !draft.lines[l.key]?.grade);
    return (
        <section aria-labelledby={`section-${section.key}`} className="space-y-3">
            <div className="flex items-center justify-between gap-3">
                <div>
                    <h3 id={`section-${section.key}`} className="text-base font-semibold">
                        {section.label}
                    </h3>
                    <p className="text-xs text-muted-foreground tabular-nums">
                        {answered} of {lines.length} answered
                    </p>
                </div>
                <Button type="button" variant={canAllOk ? 'default' : 'outline'} className="h-11 px-5" onClick={onAllOk} disabled={!canAllOk}>
                    <Check aria-hidden />
                    All OK
                </Button>
            </div>
            <div className="space-y-2">
                {lines.map((line) =>
                    pinned.has(line.key) ? (
                        <a
                            key={line.key}
                            href={`#line-${line.key}`}
                            onClick={(e) => scrollToLine(e, line.key)}
                            className={cn('flex items-center justify-between gap-2 rounded-xl border border-dashed px-3 py-3 text-sm')}
                        >
                            <span className="font-medium">{line.label}</span>
                            <span className="inline-flex items-center gap-1 text-muted-foreground">
                                {draft.lines[line.key]?.grade
                                    ? `Open item: ${GRADE_LABEL[effectiveGrade(draft.lines[line.key])!]}`
                                    : 'Open item, graded above'}
                                <CornerLeftUp className="h-4 w-4" aria-hidden />
                            </span>
                        </a>
                    ) : (
                        <div key={line.key}>{renderRow(line)}</div>
                    ),
                )}
            </div>
        </section>
    );
}
